import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// Tolerance for sub-pixel scroll offsets on scaled phone previews.
const EDGE_TOLERANCE_PX = 0.5;

type Return = {
  scrollViewRef: React.RefObject<HTMLDivElement | null>;
  onScroll: () => void;
  isScrolledToTop: boolean;
  isScrolledToBottom: boolean;
};

/**
 * Tracks whether a scroll view is at its top and/or bottom edge so callers can
 * show "more content" fades.
 */
const useScrollEdges = (): Return => {
  const scrollViewRef = useRef<HTMLDivElement>(null);
  const [isScrolledToTop, setIsScrolledToTop] = useState(true);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);
  const measure = useCallback(() => {
    const el = scrollViewRef.current;
    if (!el) return;
    setIsScrolledToTop(el.scrollTop <= EDGE_TOLERANCE_PX);
    setIsScrolledToBottom(
      el.scrollHeight <= el.scrollTop + el.clientHeight + EDGE_TOLERANCE_PX,
    );
  }, []);
  // Re-measure after every render so the fades track content changes without
  // waiting for a scroll event; the delayed pass catches layout that settles
  // after the first paint (fonts, media).
  useEffect(() => {
    measure();
    const timeout = setTimeout(measure, 100);
    return () => clearTimeout(timeout);
  });
  // Screens rarely re-render now, so media that loads late (an image above
  // long text) must re-measure on its own, or the bottom fade never appears.
  useEffect(() => {
    const el = scrollViewRef.current;
    if (!el) return;
    let cancelled = false;
    const onMediaLoad = () => {
      if (!cancelled) measure();
    };
    el.addEventListener("load", onMediaLoad, true);
    el.addEventListener("loadedmetadata", onMediaLoad, true);
    document.fonts?.ready.then(onMediaLoad);
    return () => {
      cancelled = true;
      el.removeEventListener("load", onMediaLoad, true);
      el.removeEventListener("loadedmetadata", onMediaLoad, true);
    };
  }, [measure]);
  return useMemo(
    () => ({
      scrollViewRef,
      onScroll: measure,
      isScrolledToTop,
      isScrolledToBottom,
    }),
    [measure, isScrolledToTop, isScrolledToBottom],
  );
};

export default useScrollEdges;
